/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Button,
  Divider,
  Loader,
  NumberFormatter,
  Select,
  TextInput
} from '@mantine/core';
import { Lock } from 'tabler-icons-react';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import AppCard from '../../../../common/AppCard';
import { Context as AuthContext } from '../../../../../providers/AuthContextProvider';
import { Context as HelperContext } from '../../../../../providers/HelperContextProvider';
import {
  CANADA_PROVINCE_LIST_OPTIONS,
  USA_STATE_LIST_OPTIONS
} from '../../../../../config/locationConstants';
import { useCartShippingController } from '../../../../../helpers/checkout/useCartShippingController';
import WsFinalSaleWarning from '../../blocks/finalSaleWarning/WsFinalSaleWarning';
import { isValidCanadianPostalCode } from '../../../../../helpers/vendorCatalogCheckoutHelper';

const WsCheckoutCartSummary = ({
  themeConfig,
  checkoutCart,
  mediaQueries,
  loading,
  isGuest,
  guestEmail,
  onEmailChange,
  loadingCheckout,
  loadingMockCheckout,
  disabled,
  onFundraiserChange,
  fundraiser,
  ecomStoreRosterPlayers,
  ecomStoreTheme,
  onCheckout,
  onMockCheckout,
  editModeEnabled,
  hasFinalSaleProducts,
  checkoutLineItemsInfo
}) => {
  const { isTabletOrSmaller } = mediaQueries;
  const currency = checkoutCart?.currency?.toUpperCase() ?? 'CAD';
  const { state: authState } = useContext(AuthContext);
  const { toggleWebstoreChangeShippingModal } = useContext(HelperContext);

  const {
    debouncedState,
    shippingAddress,
    shippingLoading,
    onChangeShippingAddress
  } = useCartShippingController({ fetchShippingOnAddressChange: true });

  const adminEcomStore = authState.ecomStores.find(
    (s) => s.pkEcomStore === ecomStoreTheme.ecomStore.pkEcomStore
  );
  const mockCheckoutEnabled = !!adminEcomStore?.isDemoStore;
  const pickupInsteadOfShipping =
    themeConfig.pickupInsteadOfShipping === 'true';
  const makeFundraiserInputRequired =
    themeConfig.makeFundraiserInputRequired === 'true';
  const enableCustomFundraiser = themeConfig.enableCustomFundraiser === 'true';

  const orderSummaryLoading = loading || shippingLoading;

  const isCheckoutButtonDisabled =
    disabled ||
    orderSummaryLoading ||
    checkoutCart?.lineItems.length === 0 ||
    checkoutCart?.shippingCostMissing ||
    checkoutLineItemsInfo.some(
      (i) =>
        i.isUnavailable ||
        i.requiresProductUpdate ||
        i.requiresPrintImagesUpdate
    );

  return (
    <AppCard
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onCheckout();
      }}
      shadow="lg"
      style={{
        flex: 1,
        padding: 0,
        borderRadius: isTabletOrSmaller ? 0 : 8,
        width: '100%'
      }}
      withBorder={!isTabletOrSmaller}
    >
      {((checkoutCart && !checkoutCart?.freeShipping) ||
        (ecomStoreRosterPlayers && ecomStoreRosterPlayers.length > 0)) && (
        <>
          <AppStack style={{ gap: 8, margin: isTabletOrSmaller ? 20 : 30 }}>
            <AppText style={{ fontSize: 18, fontWeight: 700 }}>
              Delivery
            </AppText>
            {checkoutCart && !checkoutCart?.freeShipping && (
              <AppStack style={{ gap: 8 }}>
                <AppStack style={{ gap: 8 }}>
                  <Select
                    clearable
                    data={[
                      {
                        label: 'Canada',
                        value: 'CA'
                      },
                      {
                        label: 'United States',
                        value: 'US'
                      }
                    ]}
                    disabled
                    label="Country"
                    onChange={(v) => {
                      if (!!v && v !== shippingAddress?.country) {
                        onChangeShippingAddress({
                          country: v,
                          state: (v === 'US'
                            ? USA_STATE_LIST_OPTIONS
                            : CANADA_PROVINCE_LIST_OPTIONS
                          ).find((s) => s.abbr === shippingAddress?.state)
                        });
                      }
                    }}
                    required
                    searchable
                    styles={{
                      input: {
                        minHeight: 0,
                        height: 30,
                        fontSize: 14
                      }
                    }}
                    value={shippingAddress?.country || ''}
                  />
                  <AppFlexbox style={{ gap: 8 }}>
                    <TextInput
                      disabled={loadingCheckout || disabled}
                      label="First name"
                      onChange={(e) => {
                        onChangeShippingAddress({firstName: e.currentTarget.value});
                      }}
                      required
                      style={{ flex: 1 }}
                      styles={{
                        input: {
                          minHeight: 0,
                          height: 30,
                          fontSize: 14
                        }
                      }}
                      value={shippingAddress?.firstName || ''}
                    />

                    <TextInput
                      disabled={loadingCheckout || disabled}
                      label="Last name"
                      onChange={(e) => {
                        onChangeShippingAddress({lastName: e.currentTarget.value});
                      }}
                      required
                      style={{ flex: 1 }}
                      styles={{
                        input: {
                          minHeight: 0,
                          height: 30,
                          fontSize: 14
                        }
                      }}
                      value={shippingAddress?.lastName || ''}
                    />
                  </AppFlexbox>

                  <TextInput
                    disabled={loadingCheckout || disabled}
                    label="Address"
                    onChange={(e) => {
                      onChangeShippingAddress({
                        address1: e.currentTarget.value
                      });
                    }}
                    required
                    style={{ flex: 1 }}
                    styles={{
                      input: {
                        minHeight: 0,
                        height: 30,
                        fontSize: 14
                      }
                    }}
                    value={shippingAddress?.address1 || ''}
                  />

                  <AppFlexbox style={{ gap: 8 }}>
                    <TextInput
                      disabled={loadingCheckout || disabled}
                      inputSize="xs"
                      label="City"
                      onChange={(e) => {
                        onChangeShippingAddress({
                          city: e.currentTarget.value
                        });
                      }}
                      required
                      style={{ flex: 1 }}
                      styles={{
                        input: {
                          minHeight: 0,
                          height: 30,
                          fontSize: 14
                        }
                      }}
                      value={shippingAddress?.city || ''}
                    />
                    <Select
                      clearable
                      data={
                        shippingAddress?.country === 'US'
                          ? USA_STATE_LIST_OPTIONS.map((s) => ({
                              label: s.label,
                              value: s.abbr
                            }))
                          : CANADA_PROVINCE_LIST_OPTIONS.map((p) => ({
                              label: p.label,
                              value: p.abbr
                            }))
                      }
                      disabled={loadingCheckout || disabled}
                      inputSize="xs"
                      label="Province"
                      onChange={(v) => {
                        if (v && v !== shippingAddress?.state) {
                          onChangeShippingAddress({ state: v });
                        }
                      }}
                      required
                      searchable
                      style={{ flex: 1 }}
                      styles={{
                        input: {
                          minHeight: 0,
                          height: 30,
                          fontSize: 14
                        }
                      }}
                      value={shippingAddress?.state || null}
                    />
                  </AppFlexbox>

                  <TextInput
                    disabled={loadingCheckout || disabled}
                    error={
                      !orderSummaryLoading &&
                      shippingAddress?.postalCode &&
                      (!debouncedState ||
                        shippingAddress?.postalCode ===
                          debouncedState?.postalCode) &&
                      !isValidCanadianPostalCode(shippingAddress?.postalCode)
                        ? 'Postal code is invalid'
                        : null
                    }
                    label="Postal code"
                    onChange={(e) => {
                      onChangeShippingAddress({
                        postalCode: e.currentTarget.value
                      });
                    }}
                    required
                    style={{ flex: 1 }}
                    styles={{
                      input: {
                        minHeight: 0,
                        height: 30,
                        fontSize: 14
                      }
                    }}
                    value={shippingAddress?.postalCode || ''}
                  />
                </AppStack>
              </AppStack>
            )}

            {ecomStoreRosterPlayers && ecomStoreRosterPlayers.length > 0 && (
              <>
                <Select
                  clearable
                  data={ecomStoreRosterPlayers.map((p) => ({
                    label: p.name,
                    value: p.pkEcomStoreRosterPlayer.toString()
                  }))}
                  description="Select the fundraiser to credit for this order."
                  disabled={loadingCheckout || disabled}
                  label="Fundraiser"
                  onChange={(v) => {
                    onFundraiserChange({ fkEcomStoreRosterPlayer: v });
                  }}
                  required={makeFundraiserInputRequired}
                  searchable
                  value={fundraiser?.fkEcomStoreRosterPlayer || null}
                />
                {enableCustomFundraiser && (
                  <TextInput
                    disabled={loadingCheckout || disabled}
                    label="Player Name"
                    onChange={(e) => {
                      onFundraiserChange({customInput: e.currentTarget.value?.substring(0, 255)});
                    }}
                    required={makeFundraiserInputRequired}
                    value={fundraiser?.customInput || ''}
                  />
                )}
              </>
            )}
          </AppStack>
          <Divider />
        </>
      )}

      <AppStack style={{ gap: 8, margin: isTabletOrSmaller ? 20 : 30 }}>
        <AppText style={{ fontSize: 18, fontWeight: 700 }}>
          Order Summary
        </AppText>
        <AppFlexbox
          style={{
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <AppText style={{ fontSize: 16 }}>Subtotal</AppText>
          {orderSummaryLoading ? (
            <Loader color="dark" size={20} type="dots" />
          ) : (
            <AppFlexbox style={{ gap: 10 }}>
              <AppText style={{ fontSize: 16 }}>
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  thousandSeparator
                  value={checkoutCart.subtotalInCents / 100}
                />
              </AppText>
              <AppText style={{ fontSize: 16 }}>{currency}</AppText>
            </AppFlexbox>
          )}
        </AppFlexbox>
        {checkoutCart?.discountTotalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Discount</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={(checkoutCart.discountTotalInCents / 100) * -1}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}

        {checkoutCart?.totalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap'
            }}
          >
            <AppStack style={{ gap: 0 }}>
              <AppText style={{ fontSize: 16 }}>Shipping</AppText>
              {!orderSummaryLoading &&
                checkoutCart.shippingCosts.length > 0 && (
                  <AppText style={{ color: '#666', fontSize: 13 }}>
                    ({checkoutCart.shippingCosts[0]?.label ?? 'Standard'})
                  </AppText>
                )}
            </AppStack>

            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                {checkoutCart.freeShipping ? (
                  pickupInsteadOfShipping ? (
                    <AppText style={{ fontsize: 16, color: '#666' }}>
                      Available for Pickup
                    </AppText>
                  ) : (
                    <AppText style={{ fontSize: 18, color: 'green' }}>
                      Free
                    </AppText>
                  )
                ) : !checkoutCart.shippingCostMissing ? (
                  <AppStack style={{ gap: 0, alignItems: 'end' }}>
                    <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                      <AppText style={{ fontSize: 16 }}>
                        <NumberFormatter
                          decimalScale={2}
                          displayType="text"
                          fixedDecimalScale
                          prefix="$"
                          thousandSeparator
                          value={checkoutCart.shippingTotalInCents / 100}
                        />
                      </AppText>
                      <AppText style={{ fontSize: 16 }}>{currency}</AppText>
                    </AppFlexbox>

                    {!disabled && !loadingCheckout && (
                      <Anchor
                        onClick={() => {
                          toggleWebstoreChangeShippingModal({ isOpen: true });
                        }}
                        style={{ color: 'dodgerblue', fontSize: 13 }}
                      >
                        Change shipping
                      </Anchor>
                    )}
                  </AppStack>
                ) : (
                  <AppText style={{ color: '#666', fontSize: 13 }}>
                    Enter shipping address
                  </AppText>
                )}
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}

        {checkoutCart?.taxTotalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Taxes</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={checkoutCart.taxTotalInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}
        {checkoutCart?.customerServiceFeeTotalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Service Fee</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={checkoutCart.customerServiceFeeTotalInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}
        {checkoutCart?.tieredPricingFeeInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Service Fee</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={checkoutCart.tieredPricingFeeInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}

        <Divider style={{ margin: '8px 0px' }} />

        <AppFlexbox
          style={{
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <AppText style={{ fontSize: 18, fontWeight: 700 }}>Total</AppText>
          {orderSummaryLoading ? (
            <Loader color="dark" size={20} type="dots" />
          ) : (
            <AppFlexbox style={{ gap: 10 }}>
              <AppText style={{ fontSize: 18, fontWeight: 700 }}>
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  thousandSeparator
                  value={checkoutCart.totalInCents / 100}
                />
              </AppText>
              <AppText style={{ fontSize: 18, fontWeight: 700 }}>
                {currency}
              </AppText>
            </AppFlexbox>
          )}
        </AppFlexbox>
      </AppStack>
      <AppStack
        style={{
          backgroundColor: '#000',
          color: '#FFF',
          opacity: 0.8,
          padding: isTabletOrSmaller ? 20 : 30,
          gap: 30
        }}
      >
        {isGuest && checkoutCart?.lineItems.length > 0 && (
          <TextInput
            autoComplete="on"
            disabled={loadingCheckout || disabled}
            label="Email"
            onChange={(e) => onEmailChange(e.currentTarget.value)}
            placeholder="Email address"
            required
            type="email"
            value={guestEmail}
          />
        )}
        <Button
          id="checkout-order-summary-submit"
          style={{ display: 'none' }}
          type="submit"
        />

        <AppStack style={{ gap: 30 }}>
          <AppStack style={{ gap: 16 }}>
            {hasFinalSaleProducts && (
              <WsFinalSaleWarning
                editModeEnabled={editModeEnabled}
                isCheckout
                mediaQueries={mediaQueries}
              />
            )}

            <Button
              disabled={loadingMockCheckout || isCheckoutButtonDisabled}
              leftSection={<Lock />}
              loading={loadingCheckout}
              onClick={() => {
                document
                  .getElementById('checkout-order-summary-submit')
                  .click();
              }}
              size="lg"
              type="button"
            >
              {isGuest ? 'Checkout as Guest' : 'Continue to Checkout'}
            </Button>
          </AppStack>

          {mockCheckoutEnabled && (
            <>
              <Divider style={{ color: '#666' }} />

              <Button
                color="dark"
                disabled={loadingCheckout || isCheckoutButtonDisabled}
                leftSection={<Lock />}
                loading={loadingMockCheckout}
                onClick={onMockCheckout}
                size="lg"
                type="button"
                variant="filled"
              >
                Checkout as mock order
              </Button>
            </>
          )}
        </AppStack>
      </AppStack>
    </AppCard>
  );
};

WsCheckoutCartSummary.propTypes = {
  checkoutCart: PropTypes.object,
  checkoutLineItemsInfo: PropTypes.array,
  colorScheme: PropTypes.object,
  disabled: PropTypes.bool,
  ecomStoreRosterPlayers: PropTypes.array,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  fundraiser: PropTypes.object,
  guestEmail: PropTypes.string,
  hasFinalSaleProducts: PropTypes.bool,
  isGuest: PropTypes.bool,
  loading: PropTypes.bool,
  loadingCheckout: PropTypes.bool,
  loadingMockCheckout: PropTypes.bool,
  mediaQueries: PropTypes.object,
  mockCheckoutEnabled: PropTypes.bool,
  onCheckout: PropTypes.func,
  onEmailChange: PropTypes.func,
  onFundraiserChange: PropTypes.func,
  onMockCheckout: PropTypes.func,
  themeConfig: PropTypes.object
};

export default WsCheckoutCartSummary;
